import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, Receipt, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { crmInvoiceOverview } from "@/lib/crm.functions";
import { formatMoney, invoiceStatusClass } from "@/components/crm/ClientInvoices";

export const Route = createFileRoute("/crm/invoices")({
  component: InvoicesPage,
});

const VIEWS = [
  { key: "all", label: "All invoices" },
  { key: "not_invoiced", label: "Not yet invoiced" },
  { key: "sent", label: "Sent" },
  { key: "unpaid", label: "Unpaid / overdue" },
  { key: "paid", label: "Paid" },
] as const;

type ViewKey = (typeof VIEWS)[number]["key"];

function InvoicesPage() {
  const overview = useServerFn(crmInvoiceOverview);
  const [view, setView] = useState<ViewKey>("all");
  const [q, setQ] = useState("");

  const query = useQuery({
    queryKey: ["crm", "invoice-overview", view, q],
    queryFn: () => overview({ data: { view, q: q || undefined } }),
  });

  const counts = query.data?.counts;
  const rows = (query.data?.invoices ?? []) as any[];
  const notInvoiced = (query.data?.not_invoiced ?? []) as any[];

  return (
    <div className="h-full overflow-y-auto bg-background pb-12">
      <header className="mx-auto max-w-6xl px-3 py-4 sm:px-4 lg:px-6">
        <h1 className="flex items-center gap-2 text-base font-bold sm:text-lg">
          <Receipt className="h-5 w-5 text-primary" aria-hidden="true" /> Invoice tracking
        </h1>
        <p className="mt-1 text-xs text-muted-foreground">
          See who has been invoiced and what is still unpaid.
        </p>
      </header>

      <main className="mx-auto max-w-6xl space-y-4 px-3 sm:px-4 lg:px-6">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Not yet invoiced" value={counts?.not_invoiced ?? 0} />
          <Stat label="Sent" value={counts?.sent ?? 0} />
          <Stat label="Unpaid / overdue" value={counts?.unpaid ?? 0} />
          <Stat label="Paid" value={counts?.paid ?? 0} />
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-border bg-card p-3">
            <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Outstanding</p>
            <p className="text-lg font-extrabold tabular-nums">
              {formatMoney(query.data?.totals?.outstanding_cents ?? 0)}
            </p>
          </div>
          <div className="rounded-xl border border-border bg-card p-3">
            <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Paid</p>
            <p className="text-lg font-extrabold tabular-nums">
              {formatMoney(query.data?.totals?.paid_cents ?? 0)}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              aria-pressed={view === v.key}
              onClick={() => setView(v.key)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
                view === v.key
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>

        <div className="relative max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            className="pl-9"
            placeholder="Search customer or invoice number"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search invoices"
          />
        </div>

        {query.isLoading ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </p>
        ) : query.error ? (
          <p className="text-sm text-destructive">{(query.error as Error).message}</p>
        ) : view === "not_invoiced" ? (
          notInvoiced.length === 0 ? (
            <Empty text="Every customer has at least one invoice record." />
          ) : (
            <ul className="grid gap-2 sm:grid-cols-2">
              {notInvoiced.map((c) => (
                <li key={c.id}>
                  <Link
                    to="/crm/$clientId"
                    params={{ clientId: c.id }}
                    className="flex items-center justify-between rounded-xl border border-border bg-card p-3 text-sm font-semibold hover:border-primary/40"
                  >
                    <span className="truncate">{c.name}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                      {c.status}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )
        ) : rows.length === 0 ? (
          <Empty text="No invoices match this view." />
        ) : (
          <ul className="space-y-2">
            {rows.map((inv) => (
              <li key={inv.id}>
                <Link
                  to="/crm/$clientId"
                  params={{ clientId: inv.client_id }}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-card p-3 hover:border-primary/40"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold">{inv.client_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {inv.invoice_number || "No reference"}
                      {inv.invoice_date ? ` · ${inv.invoice_date}` : ""}
                      {inv.due_date ? ` · due ${inv.due_date}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold tabular-nums">
                      {formatMoney(inv.amount_cents, inv.currency)}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${invoiceStatusClass(inv.effective_status)}`}
                    >
                      {String(inv.effective_status).replace("_", " ")}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-lg font-extrabold tabular-nums">{value}</p>
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return (
    <p className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
      {text}
    </p>
  );
}
